import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { CompanyService } from '@app/features/companies/company.service';
import { ContractType, ContractTypes } from '@app/features/contracts/contract.model';
import {
  VALIDATION_REQUEST_TYPES,
  ValidationRequestType,
} from '@app/features/contracts/validation/validation-request.model';
import { ValidationRequestTypeLabelPipe } from '@app/features/contracts/validation/validation-request-type-label-pipe';
import { DocumentTag, DocumentTags } from '@app/features/documents/document.model';
import { combineLatest, map, startWith, switchMap } from 'rxjs';
import { DesignationChips } from '../designation-chips/designation-chips';
import {
  contractRelationLabels,
  designationLabel,
  lineCriteria,
  lineLabel,
  NotificationEventSetting,
  NotificationPreviewPerson,
  NotificationPreviewQuery,
  NotificationPreviewReason,
  NotificationSettingLine,
  subscriptionOptionLabels,
} from '../notification-setting.model';
import { NotificationSettingService } from '../notification-setting.service';

/**
 * L'aperçu des destinataires d'un événement : pour un cas choisi, les lignes qui s'appliquent, les
 * personnes désignées à cet instant et pourquoi, celles que leur abonnement ajoute ou retire, et
 * celles qui sont écartées faute d'adresse (RG-notification-apercu-des-destinataires).
 */
@Component({
  selector: 'app-notification-preview',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    ValidationRequestTypeLabelPipe,
    DesignationChips,
  ],
  templateUrl: './notification-preview.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./notification-preview.scss'],
})
export class NotificationPreviewPanel {
  private readonly notificationSettingService = inject(NotificationSettingService);

  readonly setting = input.required<NotificationEventSetting>();

  readonly form = new FormGroup({
    companyId: new FormControl<string | null>(null),
    contractType: new FormControl<ContractType | null>(null),
    validationType: new FormControl<ValidationRequestType | null>(null),
    documentTag: new FormControl<DocumentTag | null>(null),
  });

  protected readonly companies = toSignal(inject(CompanyService).allCompanies$, {
    initialValue: [],
  });
  protected readonly contractTypes = ContractTypes;
  protected readonly validationTypes = VALIDATION_REQUEST_TYPES;
  protected readonly documentTags = DocumentTags.values;
  protected readonly documentTagLabels = DocumentTags.labels;
  protected readonly relationLabels = contractRelationLabels;

  private readonly query$ = this.form.valueChanges.pipe(
    startWith(null),
    map((): NotificationPreviewQuery => this.form.getRawValue()),
  );

  /** Recalculé à chaque cas choisi, et quand le réglage change après un enregistrement. */
  protected readonly preview = toSignal(
    combineLatest([toObservable(this.setting), this.query$]).pipe(
      switchMap(([setting, query]) =>
        this.notificationSettingService.preview(setting.event, query),
      ),
    ),
  );

  protected readonly nobody = computed(() => {
    const preview = this.preview();
    return !!preview && preview.recipients.length === 0 && preview.relations.length === 0;
  });

  protected lineLabel(lineId: string | null): string {
    return lineLabel(this.setting(), lineId);
  }

  protected criteria(line: NotificationSettingLine): string {
    return lineCriteria(line, (companyId) => this.companyName(companyId));
  }

  protected personName(person: NotificationPreviewPerson): string {
    return (
      [person.firstName, person.lastName].filter(Boolean).join(' ') || person.email || person.userId
    );
  }

  protected reasonLabel(reason: NotificationPreviewReason): string {
    if (reason.subscription) {
      const option = reason.subscriptionOption
        ? ` : ${subscriptionOptionLabels[reason.subscriptionOption]}`
        : '';
      return `S'ajoute par son abonnement${option}`;
    }
    const designation = designationLabel({
      role: reason.role,
      scope: reason.scope,
      relation: null,
    });
    return [this.lineLabel(reason.lineId), designation].filter(Boolean).join(' · ');
  }

  private companyName(companyId: string): string {
    return this.companies().find((company) => company.id === companyId)?.name ?? 'Société inconnue';
  }
}
